import { useState, useEffect } from 'react';
import { Plus, X, HardDrive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useAppContext } from '@/hooks/useAppContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';

/**
 * Lets the user view, add, remove, and reorder their BUD-03 (kind 10063)
 * Blossom server list, plus toggle whether Wikistar's own default servers
 * are merged in as a fallback. Publishes a kind 10063 event when logged in.
 * Rendered as one tab of `NetworkDialog`.
 */
export function BlossomServerListManager() {
  const { config, updateConfig } = useAppContext();
  const { user } = useCurrentUser();
  const { mutate: publishEvent } = useNostrPublish();
  const { toast } = useToast();

  const [servers, setServers] = useState<string[]>(config.blossomServerMetadata.servers);
  const [newServerUrl, setNewServerUrl] = useState('');

  // Sync local state with config when it changes (e.g., from NostrSync).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setServers(config.blossomServerMetadata.servers);
  }, [config.blossomServerMetadata.servers]);

  const normalizeServerUrl = (url: string): string => {
    url = url.trim();
    try {
      return new URL(url).toString();
    } catch {
      try {
        return new URL(`https://${url}`).toString();
      } catch {
        return url;
      }
    }
  };

  const isValidServerUrl = (url: string): boolean => {
    const trimmed = url.trim();
    if (!trimmed) return false;
    try {
      new URL(normalizeServerUrl(trimmed));
      return true;
    } catch {
      return false;
    }
  };

  const handleAddServer = () => {
    if (!isValidServerUrl(newServerUrl)) {
      toast({
        title: 'Invalid server URL',
        description: 'Please enter a valid Blossom server URL (e.g., https://blossom.example.com)',
        variant: 'destructive',
      });
      return;
    }

    const normalized = normalizeServerUrl(newServerUrl);

    if (servers.includes(normalized)) {
      toast({
        title: 'Server already exists',
        description: 'This server is already in your list.',
        variant: 'destructive',
      });
      return;
    }

    const newServers = [...servers, normalized];
    setServers(newServers);
    setNewServerUrl('');
    saveServers(newServers);
  };

  const handleRemoveServer = (url: string) => {
    const newServers = servers.filter((s) => s !== url);
    setServers(newServers);
    saveServers(newServers);
  };

  const saveServers = (newServers: string[]) => {
    // Only called from event handlers, not during render, so Date.now() is safe.
    // eslint-disable-next-line react-hooks/purity
    const now = Math.floor(Date.now() / 1000);

    updateConfig((current) => ({
      ...current,
      blossomServerMetadata: {
        servers: newServers,
        updatedAt: now,
      },
    }));

    if (user) {
      publishBUD03ServerList(newServers);
    }
  };

  const publishBUD03ServerList = (serverList: string[]) => {
    const tags = serverList.map((url) => ['server', url]);

    publishEvent(
      { kind: 10063, content: '', tags },
      {
        onSuccess: () => {
          toast({
            title: 'Server list published',
            description: 'Your Blossom server list has been published to Nostr.',
          });
        },
        onError: (error) => {
          console.error('Failed to publish Blossom server list:', error);
          toast({
            title: 'Failed to publish server list',
            description: 'There was an error publishing your server list to Nostr.',
            variant: 'destructive',
          });
        },
      },
    );
  };

  const handleToggleAppServers = (checked: boolean) => {
    updateConfig((current) => ({
      ...current,
      useAppBlossomServers: checked,
    }));
  };

  const renderServerUrl = (url: string): string => {
    try {
      const parsed = new URL(url);
      return parsed.pathname === '/' ? parsed.host : parsed.host + parsed.pathname;
    } catch {
      return url;
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {servers.map((url) => (
          <div key={url} className="flex items-center gap-3 rounded-md border bg-muted/20 p-3">
            <HardDrive className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 truncate font-mono text-sm" title={url}>
              {renderServerUrl(url)}
            </span>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleRemoveServer(url)}
              className="size-5 shrink-0 text-muted-foreground hover:bg-transparent hover:text-destructive"
              disabled={servers.length <= 1}
            >
              <X className="size-4" />
            </Button>
          </div>
        ))}
        {servers.length === 0 && (
          <p className="rounded-md border border-dashed p-3 text-center text-sm text-muted-foreground">
            No servers added yet. Wikistar's default servers are used as a fallback.
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <div className="flex-1">
          <Label htmlFor="new-server-url" className="sr-only">
            Server URL
          </Label>
          <Input
            id="new-server-url"
            placeholder="https://blossom.example.com"
            value={newServerUrl}
            onChange={(e) => setNewServerUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddServer();
              }
            }}
          />
        </div>
        <Button
          onClick={handleAddServer}
          disabled={!newServerUrl.trim()}
          variant="outline"
          size="sm"
          className="h-10 shrink-0"
        >
          <Plus className="size-4" />
          Add
        </Button>
      </div>

      <div className="flex items-center justify-between rounded-md border bg-muted/20 p-3">
        <div className="pr-3">
          <Label htmlFor="use-app-servers" className="cursor-pointer text-sm">
            Include Wikistar's default servers
          </Label>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Used as a fallback alongside your own servers when uploading files.
          </p>
        </div>
        <Switch
          id="use-app-servers"
          checked={config.useAppBlossomServers}
          onCheckedChange={handleToggleAppServers}
        />
      </div>

      {!user && (
        <p className="text-xs text-muted-foreground">
          Log in to publish your server list to Nostr (BUD-03).
        </p>
      )}
    </div>
  );
}
